import * as React from 'react';
import { I18nProvider } from '@lingui/react';
import { i18n, activateLocale, defaultLocaleFor, type Locale } from '@viewstream/i18n';

const STORAGE_KEY = 'vs.locale'; // UI preference only (never a credential); the user's saved locale from /v1/me wins

interface LocaleCtx {
  locale: Locale; tz: string; setLocale: (l: Locale) => void;
  /** Tenant defaults (Settings → Defaults): the time zone always, the language only when the person never chose one. */
  applyTenantDefaults: (locale: Locale, tz: string) => void;
}
const Ctx = React.createContext<LocaleCtx>({ locale: 'he', tz: 'Asia/Jerusalem', setLocale: () => {}, applyTenantDefaults: () => {} });

/** The person chose a language (?lang= or the language switch); the tenant default then never overrides it. */
export function hasExplicitLocale(): boolean {
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (fromUrl === 'he' || fromUrl === 'en') return true;
  try { const s = localStorage.getItem(STORAGE_KEY); return s === 'he' || s === 'en'; } catch { return false; }
}

export function pickInitialLocale(): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (fromUrl === 'he' || fromUrl === 'en') return fromUrl;
  try { const s = localStorage.getItem(STORAGE_KEY); if (s === 'he' || s === 'en') return s; } catch { /* private mode */ }
  return defaultLocaleFor(window.location.hostname);
}

export function LocaleProvider({ children, initial, tz: initialTz }: { children: React.ReactNode; initial: Locale; tz?: string }) {
  const [locale, setLocaleState] = React.useState<Locale>(initial);
  const [tz, setTz] = React.useState(initialTz ?? 'Asia/Jerusalem');
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => { void activateLocale(locale).then(() => setReady(true)); }, [locale]);
  const setLocale = React.useCallback((l: Locale) => { try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ } setLocaleState(l); }, []);
  const applyTenantDefaults = React.useCallback((l: Locale, z: string) => {
    if (z) setTz(z);
    if (!hasExplicitLocale()) setLocaleState(l);
  }, []);
  const value = React.useMemo(() => ({ locale, tz, setLocale, applyTenantDefaults }), [locale, tz, setLocale, applyTenantDefaults]);
  if (!ready) return null;
  return <Ctx.Provider value={value}><I18nProvider i18n={i18n}>{children}</I18nProvider></Ctx.Provider>;
}
export function useLocale(): LocaleCtx { return React.useContext(Ctx); }
